import { useState, useCallback, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const useSolicitudesFilters = (solicitudes) => {
  // Estados para filtros de fecha rápidos
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatusFilter, setActiveStatusFilter] = useState(null);
  
  // Estados para filtros de tabla
  const [tableFilteredData, setTableFilteredData] = useState(null);
  const [tableFilters, setTableFilters] = useState({});
  const [currentPagination, setCurrentPagination] = useState({ current: 1, pageSize: 10 });
  const tableRef = useRef();

  // Función para filtrar por fecha
  const getDateFilteredData = useCallback((data, filter) => {
    if (!filter || filter === 'all') return data;
    
    const now = dayjs();
    const today = now.startOf('day');
    
    return data.filter(solicitud => {
      const fechaSolicitud = dayjs(solicitud.fechaCreacion);
      
      switch (filter) {
        case 'today':
          return fechaSolicitud.isSame(today, 'day');
        case 'week':
          const startOfWeek = now.startOf('week');
          const endOfWeek = now.endOf('week');
          return fechaSolicitud.isBetween(startOfWeek, endOfWeek, 'day', '[]');
        case 'month':
          return fechaSolicitud.isSame(now, 'month');
        case 'lastMonth':
          const lastMonth = now.subtract(1, 'month');
          return fechaSolicitud.isSame(lastMonth, 'month');
        default:
          return true;
      }
    });
  }, []);

  // Función para filtrar por búsqueda
  const getSearchFilteredData = useCallback((data, term) => {
    if (!term || !term.trim()) return data;
    
    const searchLower = term.toLowerCase().trim();
    return data.filter(solicitud => 
      solicitud.comentario?.toLowerCase().includes(searchLower) ||
      solicitud.proveedorSolicitudID?.toString().includes(searchLower) ||
      solicitud.proveedorUsuarioNombre?.toLowerCase().includes(searchLower)
    );
  }, []);

  // Función para filtrar por estado
  const getStatusFilteredData = useCallback((data, statusFilter) => {
    if (!statusFilter) return data;
    
    switch (statusFilter) {
      case 'abiertas':
        return data.filter(sol => sol.estatus === 'Abierto');
      case 'cerradas':
        return data.filter(sol => sol.estatus === 'Cerrado');
      case 'pendientes':
        return data.filter(sol => sol.estatus === 'En proceso' || sol.estatus === 'Pendiente');
      default:
        return data;
    }
  }, []);

  // Calcular datos filtrados
  const filteredData = useMemo(() => {
    let data = [...solicitudes];
    
    // Aplicar filtro de fecha
    data = getDateFilteredData(data, activeFilter);
    
    // Aplicar filtro de búsqueda
    data = getSearchFilteredData(data, searchTerm);
    
    // Aplicar filtro de estado
    data = getStatusFilteredData(data, activeStatusFilter);
    
    return data;
  }, [solicitudes, activeFilter, searchTerm, activeStatusFilter, getDateFilteredData, getSearchFilteredData, getStatusFilteredData]);


  // Función para obtener información completa de filtros
  const getCompleteFilterInfo = useCallback(() => {
    let label = '';
    let count = filteredData.length;
    let hasFilters = false;
    
    if (activeFilter && activeFilter !== 'all') {
      const filterLabels = {
        'today': 'Solicitudes de hoy',
        'week': 'Solicitudes de esta semana',
        'month': 'Solicitudes de este mes',
        'lastMonth': 'Solicitudes del mes pasado'
      };
      label = filterLabels[activeFilter] || activeFilter;
      hasFilters = true;
    }
    
    // Verificar filtros de tabla activos
    const hasTableFilters = Object.keys(tableFilters).some(key => {
      const values = tableFilters[key];
      return values && values.length > 0 && values[0] !== '' && values[0] !== null && values[0] !== undefined;
    });

    if (hasTableFilters) {
      count = tableFilteredData ? tableFilteredData.length : filteredData.length;
      hasFilters = true;
      
      const activeTableFilters = [];
      
      Object.entries(tableFilters).forEach(([key, values]) => {
        if (values && values.length > 0 && values[0] !== '' && values[0] !== null && values[0] !== undefined) {
          const textSearchFields = ['proveedorSolicitudID', 'comentario'];
          
          if (textSearchFields.includes(key)) {
            activeTableFilters.push(`Búsqueda: "${values[0]}"`);
          } else if (key === 'estatus') {
            activeTableFilters.push(`Estado: ${values[0]}`);
          } else {
            activeTableFilters.push(`${key}: ${values[0]}`);
          }
        }
      });
      
      if (activeTableFilters.length > 0) {
        if (label) {
          label = `${label} - ${activeTableFilters.join(' | ')}`;
        } else {
          label = activeTableFilters.join(' | ');
        }
      }
    }
    
    // Agregar filtros de búsqueda
    if (searchTerm) {
      const searchLabel = `Búsqueda global: "${searchTerm}"`;
      label = label ? `${label} - ${searchLabel}` : searchLabel;
      hasFilters = true;
    }
    
    // Agregar filtros de estado
    if (activeStatusFilter) {
      const statusLabels = {
        'abiertas': 'Solo abiertas',
        'cerradas': 'Solo cerradas',
        'pendientes': 'Solo pendientes'
      };
      const statusLabel = statusLabels[activeStatusFilter] || activeStatusFilter;
      label = label ? `${label} - ${statusLabel}` : statusLabel;
      hasFilters = true;
    }
    
    if (!hasFilters) return {};
    
    return {
      label: label,
      count,
      isActive: hasFilters
    };
  }, [filteredData, activeFilter, searchTerm, activeStatusFilter, tableFilters, tableFilteredData]);

  // Función para manejar click en filtro de estado
  const handleStatusFilterClick = useCallback((status) => {
    setActiveStatusFilter(activeStatusFilter === status ? null : status);
  }, [activeStatusFilter]);

  // Función para resetear todos los filtros
  const handleResetAllFilters = useCallback(() => {
    setActiveFilter('all');
    setSearchTerm('');
    setActiveStatusFilter(null);
    
    if (tableRef.current && tableRef.current.clearAllFilters) {
      tableRef.current.clearAllFilters();
    }
    
    setTableFilteredData(null);
    setTableFilters({});
    setCurrentPagination({ current: 1, pageSize: 10 });
  }, []);

  // Función para obtener datos finales
  const getFinalData = useCallback(() => {
    return tableFilteredData || filteredData;
  }, [tableFilteredData, filteredData]);

  // Información completa de filtros calculada
  const completeFilterInfo = useMemo(() => getCompleteFilterInfo(), [getCompleteFilterInfo]);

  // Función para manejar cambios de tabla
  const handleTableChange = useCallback(({ filteredData, filters, sorter, pagination }) => {
    setCurrentPagination(pagination);
    
    const hasActiveFilters = Object.keys(filters).some(key => {
      const values = filters[key];
      return values && values.length > 0 && values[0] !== '' && values[0] !== null && values[0] !== undefined;
    });
    
    setTableFilters(filters);
    
    if (hasActiveFilters) {
      setTableFilteredData(filteredData);
    } else {
      setTableFilteredData(null);
    }
  }, []);

  // Verificar si hay filtros activos
  const isFiltered = useMemo(() => {
    return activeFilter !== 'all' || 
           searchTerm.trim() !== '' || 
           activeStatusFilter !== null ||
           Object.keys(tableFilters).some(key => {
             const values = tableFilters[key];
             return values && values.length > 0 && values[0] !== '' && values[0] !== null && values[0] !== undefined;
           });
  }, [activeFilter, searchTerm, activeStatusFilter, tableFilters]);

  return {
    // Datos
    filteredData: getFinalData(),
    
    // Información de filtros
    filterInfo: completeFilterInfo,
    isFiltered,
    
    // Estados de filtros
    activeFilter,
    setActiveFilter,
    activeStatusFilter,
    setActiveStatusFilter,
    searchTerm,
    setSearchTerm,
    tableFilters,
    currentPagination,
    
    // Referencias
    tableRef,
    
    // Funciones de manejo
    handleTableChange,
    handleStatusFilterClick,
    handleResetAllFilters
  };
};